/**
 * 导航工具函数
 */

import { isIOS } from './deviceUtils';
import { isAppVersionGreaterThanOrEqual, kAndroidWazeVersion } from './versionUtils';
import i18n from '../i18n';

export type NavigationMapType = 'Google' | 'Apple' | 'Inapp' | 'Waze';
export type NavigationAppStorageKey = 'google' | 'apple' | 'inapp' | 'waze';

/**
 * 地图类型映射表：从 localStorage 的 key 转换为 MapType
 */
const MAP_TYPE_MAP: Record<NavigationAppStorageKey, NavigationMapType> = {
    'apple': 'Apple',
    'google': 'Google',
    'inapp': 'Inapp',
    'waze': 'Waze'
};

/**
 * 从 localStorage 获取导航应用并转换为 MapType
 * @param defaultType 默认类型，如果 localStorage 中没有值或值无效时使用
 * @returns 导航地图类型
 */
export function getNavigationMapTypeFromStorage(defaultType: NavigationMapType = 'Google'): NavigationMapType {
    const defaultNavigationApp = localStorage.getItem('navigationApp') || 'google';
    if (defaultNavigationApp === 'apple' || defaultNavigationApp === 'google' || defaultNavigationApp === 'inapp' || defaultNavigationApp === 'waze') {
        return MAP_TYPE_MAP[defaultNavigationApp as NavigationAppStorageKey];
    }
    return defaultType;
}

/**
 * 打开 URL（用于非 Android 环境）
 */
const openUrl = (url: string): void => {
    window.location.href = url;
};

/**
 * 检查是否启用长按功能
 * Android >= 8.1.0 时启用长按功能，iOS 始终启用
 * @returns Promise<boolean> 是否启用长按功能
 */
export async function checkLongPressEnabled(): Promise<boolean> {
    try {
        // iOS 始终启用，Android 需要版本 >= 8.1.0
        return isIOS() || await isAppVersionGreaterThanOrEqual(kAndroidWazeVersion);
    } catch (error) {
        console.error('Failed to check long press enabled:', error);
        return false;
    }
}

/**
 * 打开 Waze 导航
 * @param address 地址字符串
 * @param latitude 纬度（可选）
 * @param longitude 经度（可选）
 */
export function openWazeNavigation(
    address: string,
    latitude?: number,
    longitude?: number
): void {
    // 输入验证
    if (!address || address.trim() === '') {
        console.warn('openWazeNavigation: address is empty or invalid');
        return;
    }

    // 验证经纬度是否有效
    const hasValidCoords = latitude && longitude &&
                          !isNaN(latitude) && !isNaN(longitude) &&
                          latitude >= -90 && latitude <= 90 &&
                          longitude >= -180 && longitude <= 180;

    // 构建 Waze URL
    const wazeAppUrl = hasValidCoords
        ? `waze://?ll=${latitude},${longitude}&navigate=yes`
        : `waze://?q=${encodeURIComponent(address.trim())}&navigate=yes`;

    // Android 使用 JS-Bridge，iOS 使用 openUrl，其他设备使用网页版
    if (typeof window !== 'undefined' && (window as any).AndroidApp?.openNavigation) {
        // Android：通过原生方法打开，原生端会检测应用是否安装并处理 fallback
        (window as any).AndroidApp.openNavigation(wazeAppUrl);
    } else if (isIOS()) {
        // iOS：使用 openUrl，TTWebView.swift 会处理 fallback（跳转到 App Store）
        openUrl(wazeAppUrl);
    } else {
        // 其他设备（浏览器环境）：使用网页版 Waze
        const wazeWebUrl = hasValidCoords
            ? `https://www.waze.com/ul?ll=${latitude},${longitude}&navigate=yes`
            : `https://www.waze.com/ul?q=${encodeURIComponent(address.trim())}&navigate=yes`;
        openUrl(wazeWebUrl);
    }
}

/**
 * 获取导航地图选项的翻译文本数组
 * 用于识别导航弹窗
 * @returns 导航地图选项的翻译文本数组
 */
export function getNavigationMapKeys(): string[] {
    return [
        i18n.t('pages.delivery.orderDetail.googleMaps'),
        i18n.t('pages.delivery.orderDetail.appleMaps'),
        i18n.t('pages.delivery.orderDetail.wazeMaps'),
        i18n.t('pages.delivery.orderDetail.mapBoxMaps')
    ];
}

/**
 * 获取取消按钮的翻译文本
 * @returns 取消按钮的翻译文本
 */
export function getCancelButtonText(): string {
    return i18n.t('pages.delivery.orderDetail.mapCancel');
}

/**
 * 发送全局事件通知所有导航组件关闭弹窗
 * 解决集群模式下无法通过 ref 关闭 React 状态的问题
 */
export function emitCloseNavigationEvent(): void {
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('close-all-navigation-modals'));
    }
}

/**
 * 关闭所有打开的导航弹窗
 * 通过 DOM 查询找到所有导航弹窗并尝试关闭它们
 * @param navigationMapKeys 导航地图选项的翻译文本数组，用于识别导航弹窗（可选，如果不提供则自动获取）
 * @param cancelButtonText 取消按钮的翻译文本（可选，如果不提供则自动获取）
 */
export function closeNavigationModals(
    navigationMapKeys?: string[],
    cancelButtonText?: string
): void {
    // 安全检查：确保在浏览器环境中执行
    if (typeof document === 'undefined') return;
    
    // 如果没有提供参数，自动获取
    const finalNavigationMapKeys = navigationMapKeys || getNavigationMapKeys();
    const finalCancelButtonText = cancelButtonText || getCancelButtonText();
    
    // 安全检查：确保翻译键已准备好
    if (!finalNavigationMapKeys || finalNavigationMapKeys.length === 0) return;
    
    try {
        const modals = document.querySelectorAll('.adm-modal');
        modals.forEach(modal => {
            try {
                // 检查 Modal 是否可见
                const modalElement = modal as HTMLElement;
                if (!modalElement || modalElement.style.display === 'none' || !modalElement.offsetParent) {
                    return; // 跳过不可见的弹窗
                }
                // 检查 Modal 内容是否包含导航选项（通过翻译文本判断）
                const modalContent = modal.textContent || '';
                const isNavigationModal = finalNavigationMapKeys.some(key => key && modalContent.includes(key));
                if (isNavigationModal) {
                    // 找到导航弹窗，尝试关闭（按优先级尝试多种方法，iOS 18 需要增强）
                    // 方法1：查找取消按钮并点击（使用 iOS 18 增强逻辑）
                    const cancelButton = Array.from(modal.querySelectorAll('button, .adm-button')).find(
                        btn => {
                            const btnText = btn.textContent || '';
                            return (finalCancelButtonText && btnText.includes(finalCancelButtonText)) || btnText.includes('Cancel');
                        }
                    );
                    if (cancelButton) {
                        const btnElement = cancelButton as HTMLElement;
                        // iOS 18 可能需要多种方式触发
                        // 方式1：直接 click（同步执行）
                        btnElement.click();
                        // 方式2：延迟再次点击（iOS 18 可能需要）
                        setTimeout(() => {
                            if (btnElement && btnElement.offsetParent) {
                                btnElement.click();
                            }
                        }, 50);
                        // 方式3：如果点击无效，直接隐藏（iOS 18 的后备方案）
                        setTimeout(() => {
                            if (modalElement && modalElement.offsetParent) {
                                modalElement.style.display = 'none';
                                const mask = modal.closest('.adm-mask');
                                if (mask) {
                                    (mask as HTMLElement).style.display = 'none';
                                }
                            }
                        }, 200);
                        return; // 已处理，继续处理下一个弹窗
                    }
                    // 方法2：查找关闭按钮
                    const closeButton = modal.querySelector('.adm-modal-close, .adm-modal-action-button');
                    if (closeButton) {
                        const closeBtnElement = closeButton as HTMLElement;
                        closeBtnElement.click();
                        setTimeout(() => {
                            if (closeBtnElement && closeBtnElement.offsetParent) {
                                closeBtnElement.click();
                            }
                        }, 50);
                        setTimeout(() => {
                            if (modalElement && modalElement.offsetParent) {
                                modalElement.style.display = 'none';
                                const mask = modal.closest('.adm-mask');
                                if (mask) {
                                    (mask as HTMLElement).style.display = 'none';
                                }
                            }
                        }, 200);
                        return; // 已处理，继续处理下一个弹窗
                    }
                    // 方法3：直接触发遮罩层点击（如果支持，iOS 18 增强）
                    const mask = modal.closest('.adm-mask') || document.querySelector('.adm-mask');
                    if (mask) {
                        const maskElement = mask as HTMLElement;
                        // 方式1：直接 click
                        maskElement.click();
                        // 方式2：延迟再次点击（iOS 18 可能需要）
                        setTimeout(() => {
                            if (maskElement && maskElement.offsetParent) {
                                maskElement.click();
                            }
                        }, 50);
                        // 方式3：直接设置 display 为 none（最后的后备方案，iOS 18 可能需要）
                        setTimeout(() => {
                            if (maskElement && maskElement.offsetParent) {
                                maskElement.style.display = 'none';
                                if (modalElement) {
                                    modalElement.style.display = 'none';
                                }
                            }
                        }, 200);
                    } else {
                        // 如果没有找到遮罩层，直接隐藏 Modal（iOS 18 的后备方案）
                        setTimeout(() => {
                            if (modalElement && modalElement.offsetParent) {
                                modalElement.style.display = 'none';
                            }
                        }, 200);
                    }
                }
            } catch (error) {
                // 单个弹窗处理出错时，继续处理下一个
                console.warn('Error closing navigation modal:', error);
            }
        });
    } catch (error) {
        // DOM 查询出错时，静默处理，不影响主流程
        console.warn('Error querying navigation modals:', error);
    }
}

/**
 * 强制关闭导航弹窗（通过 DOM 操作）
 * 用于在状态更新后确保 Modal 能够正确关闭，特别是在 iOS 上
 * @param titleText 弹窗标题文本，用于识别导航弹窗
 * @param cancelButtonText 取消按钮文本，用于点击关闭
 */
export function forceCloseNavigationModal(
    titleText?: string,
    cancelButtonText?: string
): void {
    // 安全检查：确保在浏览器环境中执行
    if (typeof document === 'undefined') return;
    
    // 如果没有提供参数，使用默认值
    const finalTitleText = titleText || getNavigationMapKeys()[0];
    const finalCancelButtonText = cancelButtonText || getCancelButtonText();
    
    // 安全检查：确保参数已准备好
    if (!finalTitleText) return;
    
    try {
        const modals = document.querySelectorAll('.adm-modal');
        modals.forEach(modal => {
            try {
                const modalElement = modal as HTMLElement;
                // 检查 Modal 是否可见
                if (!modalElement || !modalElement.offsetParent) {
                    return; // 跳过不可见的弹窗
                }
                // 检查是否是导航弹窗（通过标题判断）
                const modalContent = modal.textContent || '';
                if (modalContent.includes(finalTitleText)) {
                    // 方法1：尝试点击取消按钮（使用多种方式触发）
                    const cancelButton = Array.from(modal.querySelectorAll('button, .adm-button')).find(
                        btn => {
                            const btnText = btn.textContent || '';
                            return (finalCancelButtonText && btnText.includes(finalCancelButtonText)) || btnText.includes('Cancel');
                        }
                    );
                    if (cancelButton) {
                        const btnElement = cancelButton as HTMLElement;
                        // iOS 18 可能需要多种方式触发
                        // 方式1：直接 click（同步执行）
                        btnElement.click();
                        // 方式2：延迟再次点击（iOS 18 可能需要）
                        setTimeout(() => {
                            if (btnElement && btnElement.offsetParent) {
                                btnElement.click();
                            }
                        }, 50);
                        // 方式3：如果点击无效，直接隐藏（iOS 18 的后备方案）
                        setTimeout(() => {
                            if (modalElement && modalElement.offsetParent) {
                                modalElement.style.display = 'none';
                                const mask = modal.closest('.adm-mask');
                                if (mask) {
                                    (mask as HTMLElement).style.display = 'none';
                                }
                            }
                        }, 200);
                        return;
                    }
                    // 方法2：尝试点击遮罩层（使用多种方式）
                    const mask = modal.closest('.adm-mask') || document.querySelector('.adm-mask');
                    if (mask) {
                        const maskElement = mask as HTMLElement;
                        // 方式1：直接 click
                        maskElement.click();
                        // 方式2：延迟再次点击（iOS 18 可能需要）
                        setTimeout(() => {
                            if (maskElement && maskElement.offsetParent) {
                                maskElement.click();
                            }
                        }, 50);
                        // 方式3：直接设置 display 为 none（最后的后备方案，iOS 18 可能需要）
                        setTimeout(() => {
                            if (maskElement && maskElement.offsetParent) {
                                maskElement.style.display = 'none';
                                if (modalElement) {
                                    modalElement.style.display = 'none';
                                }
                            }
                        }, 200);
                    } else {
                        // 如果没有找到遮罩层，直接隐藏 Modal（iOS 18 的后备方案）
                        setTimeout(() => {
                            if (modalElement && modalElement.offsetParent) {
                                modalElement.style.display = 'none';
                            }
                        }, 200);
                    }
                }
            } catch (error) {
                // 单个弹窗处理出错时，继续处理下一个
                console.warn('Error force closing navigation modal:', error);
            }
        });
    } catch (error) {
        // DOM 查询出错时，静默处理，不影响主流程
        console.warn('Error querying navigation modals for force close:', error);
    }
}
